import 'mapbox-gl/dist/mapbox-gl.css';

import { cn, Container } from 'component-library';
import { useMemo, useRef, useState } from 'react';
import { MapRef } from 'react-map-gl';

import { Plot } from '@/api/rest/resources/types/plot';
import { Form, Map } from '@/components';
import { useBoundingBox } from '@/components/MapOverview/hooks/useBoundingBox';
import { DEFAULT_MAP_STYLE, MAP_MAX_ZOOM, MAP_OVERVIEW_PADDING_MOBILE } from '@/config/constants';
import { useYearsRangeForHabitatIntactness } from '@/hooks/useYearsRangeForHabitatIntactness';
import { INTERACTIVE_LAYERS } from '@/pages/shared/plots/pages/constants';
import {
  InteractiveMapFormFields,
  useInteractiveMapControlsForm,
} from '@/pages/shared/plots/pages/hooks/useControlsForm';
import { usePlotCells } from '@/pages/shared/plots/pages/hooks/usePlotCells';
import { getGeometriesForPlots } from '@/utils/bounds';
import { isTestEnv } from '@/utils/isTestEnv';

import { InfoPanel } from './InfoPanel';
import { InteractiveMapLayers } from './layers/InteractiveMapLayers';
import { MapControls } from './MapControls';
import { PlotCells } from './PlotCells';

export const PlotInteractiveMap = ({ plot }: { plot: Plot }) => {
  /**
   * The Map's onLoad callback is not triggered in the test environment, and so
   * the map remains hidden. We set the default value of isMapReady to true in
   * the test environment.
   */
  const [isMapReady, setIsMapReady] = useState(isTestEnv);

  const mapRef = useRef<MapRef | null>(null);

  const yearsRange = useYearsRangeForHabitatIntactness();

  const controlsForm = useInteractiveMapControlsForm({
    defaultValues: {
      [InteractiveMapFormFields.Year]: yearsRange ? `${yearsRange[1]}` : undefined,
      [InteractiveMapFormFields.ThreatFilters]: [],
      [InteractiveMapFormFields.IndicatorLayer]: undefined,
    },
  });

  const plotCells = usePlotCells();

  const geometries = useMemo(() => getGeometriesForPlots([plot]), [plot]);
  const { bounds } = useBoundingBox({
    geometries,
    padding: MAP_OVERVIEW_PADDING_MOBILE,
  });

  const initialViewState = {
    bounds,
    fitBoundsOptions: { padding: MAP_OVERVIEW_PADDING_MOBILE, maxZoom: MAP_MAX_ZOOM },
  };

  const cell = controlsForm.watch(InteractiveMapFormFields.Cell);

  return (
    <Form {...controlsForm}>
      <Container
        gutterWidth={{ sm: 4, md: 10 }}
        className='h-[calc(100%-64px-48px)] w-full py-8' // py-8 = 64px + py-6 = 48px (in SecondaryNav)
        data-testid='plot-interactive-map-page'
      >
        <div
          className={cn(
            'relative h-full w-full overflow-hidden rounded-3xl bg-transparent',
            !isMapReady && 'animate-pulse bg-info-light',
          )}
        >
          <Map
            ref={mapRef}
            data-testid='plot-interactive-map-gl'
            initialViewState={initialViewState}
            onLoad={(e) => {
              /**
               * The map randomly renders half-blank.
               * The following steps prevent that from happening.
               */
              e.target.resize();
              e.target.fitBounds(initialViewState.bounds, initialViewState.fitBoundsOptions);

              setIsMapReady(true);
            }}
            interactiveLayerIds={INTERACTIVE_LAYERS}
            style={{ opacity: isMapReady ? 1 : 0, transition: 'opacity 0.2s', width: '100%', height: '100%' }}
            mapStyle={DEFAULT_MAP_STYLE}
            logoPosition='bottom-left'
          >
            <InteractiveMapLayers />
            <MapControls className='absolute left-6 top-6' />
            {plotCells && <PlotCells />}
            {cell && (
              <div className='absolute right-0 top-0 h-full animate-in fade-in slide-in-from-right-[10%]'>
                <InfoPanel />
              </div>
            )}
          </Map>
        </div>
      </Container>
    </Form>
  );
};
